// Shared helpers for the mission-visits API actions. Centralises the
// build-context + wrap-error logic so each action call stays a one-
// liner and the wrap-error rule is consistent across the surface.

import { ServiceHttpError, getAuthToken } from '../http/serviceLayerClient'

export interface RequestContext {
  token: string | null
  actorEmail: string | null
}

// Builds the per-request auth + actor context. Reads the actor email
// from the in-flight `window.__USER__` global so background actions
// still have an actor identity even when React state is mid-tear-
// down.
export async function buildCtx(): Promise<RequestContext> {
  const token = await getAuthToken()
  const user = (window as unknown as { __USER__?: { email: string | null } }).__USER__
  return { token, actorEmail: user?.email ?? null }
}

// Wraps an action so that ServiceHttpError surfaces as a friendly
// Error with a domain prefix (e.g. "Failed to record visit: <details>"),
// while any other error rethrows unchanged. The label appears in the
// user-facing toast at the call site, so it must be concise.
//
// Replaces the repeated try/catch block that wrapped every action in
// missionVisits.ts. Centralising it keeps the rule consistent and
// makes the wrapper itself unit-testable without spinning up the
// service layer.
export async function withWrappedError<T>(label: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (error instanceof ServiceHttpError) {
      // Preserve the HTTP status (+ code + details + original) on the wrapped
      // error so downstream classifiers can still distinguish a replayable
      // transport/5xx failure from a genuine 4xx client rejection — most
      // importantly the visit-report offline-first path (isReplayableSubmitFailure:
      // queue on transport/5xx, hard-fail on 4xx). Without carrying .status, the
      // wrapped plain Error looks status-less and EVERY failure classifies as
      // replayable → a 4xx report would be queued for blind replay forever
      // (Codex P1 on #1932). Additive + harmless to status-agnostic callers.
      //
      // .details is the S1787 fix (BUG-2026-07-21-S1787): SL wraps a Postgres
      // CHECK-constraint violation as an outer 502 …_RPC_FAILED whose upstream
      // SQLSTATE (23514 ck_evd_branche_correction) rides on
      // `details.upstream.code`. `isDeterministicUpstream` reads ONLY `err.details`
      // — it does NOT traverse `err.cause`. Stripping .details here made every
      // deterministic 502 look like a transient server blip → the offline entry
      // re-pended forever instead of dead-lettering for recovery (the immortal
      // loop). Carrying .details lets the classifier see the 23514 and dead-letter.
      const wrapped = new Error(`${label}: ${error.message}`) as Error & {
        status?: number
        code?: string
        details?: Record<string, unknown>
        cause?: unknown
      }
      wrapped.status = error.status
      wrapped.code = error.code
      wrapped.details = error.details
      wrapped.cause = error
      throw wrapped
    }
    throw error
  }
}
